// Scoped copy of the approved Form Spam preview table. Keep other application tables independent.
import {
	forwardRef,
	LegacyRef,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react'
import { useTable } from 'react-table'
import { TableVirtuoso } from 'react-virtuoso'
import { twMerge } from 'tailwind-merge'

import { RiArrowRightSLine, RiErrorWarningLine } from 'react-icons/ri'

import { DotMatrix } from '@clickguard/design-system'
import { TableBody } from './table-body'
import { TableHeader } from './table-header'
import { GTableProps } from './types'
import { useVirtuosoScrollParent } from './use-virtuoso-scroll-parent'

/** Left chevron cell for an expandable row (only rendered when `renderSubRow` is set). */
function ExpandToggleCell({
	expanded,
	onToggle,
	isDs
}: {
	expanded: boolean
	onToggle: () => void
	isDs: boolean
}) {
	return (
		<td
			className={twMerge(
				'align-middle',
				isDs
					? 'w-[30px] pl-[6px] pr-0 py-4 border-b border-b-[rgb(var(--cg-border))]'
					: 'w-10 px-2 py-4'
			)}
		>
			<button
				type="button"
				aria-label={expanded ? 'Collapse row' : 'Expand row'}
				aria-expanded={expanded}
				onClick={onToggle}
				className={
					isDs
						? 'flex h-6 w-6 items-center justify-center rounded-md border border-[rgb(var(--cg-border))] bg-white text-[#29282a] shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)] hover:bg-[#f9f9f9]'
						: 'flex items-center justify-center text-[#29282a] hover:text-[rgb(var(--cg-text-secondary))]'
				}
			>
				<RiArrowRightSLine
					className={twMerge(
						'h-4 w-4 transition-transform',
						expanded ? 'rotate-90' : ''
					)}
				/>
			</button>
		</td>
	)
}


/**
 * TableVirtuoso `components` must keep the SAME identity across renders.
 * The old inline definitions made a new component type on every GTable
 * render, so React unmounted and remounted the whole <table> whenever the
 * data changed. That killed row hover, focus, and every CSS transition
 * inside a cell (the Status Toggle hard-cut instead of sliding). The
 * per-render values these components need travel through virtuoso's
 * `context` prop instead, which updates without a remount.
 */
type VirtuosoTableContext = {
	tableProps: any
	bodyProps: any
	showHeader: boolean
	displayItems: Array<{ kind: 'row' | 'sub'; rowIndex: number }> | null
	rows: any[]
	data: any[]
	rowClassName?: (item: any) => string
}

const VirtuosoTable = ({ style, context, ...props }: any) => (
	<table
		className="min-w-full table-auto"
		{...(context as VirtuosoTableContext).tableProps}
		{...props}
		style={{ ...style, tableLayout: 'fixed' }}
	/>
)

const VirtuosoTableBody = forwardRef(
	({ style: _style, context, ...props }: any, ref) => (
		<tbody
			className={twMerge(
				(context as VirtuosoTableContext).showHeader
					? 'divide-y divide-t-checkbox border-b border-b-t-checkbox'
					: ''
			)}
			{...(context as VirtuosoTableContext).bodyProps}
			{...props}
			ref={ref as LegacyRef<HTMLTableSectionElement> | undefined}
		/>
	)
) as any

const VirtuosoTableHead = forwardRef(
	({ style: _style, context: _context, ...props }: any, ref) => (
		<thead
			className="sticky top-0 z-5 text-t-desc-gray"
			{...props}
			ref={ref as LegacyRef<HTMLTableSectionElement> | undefined}
		/>
	)
) as any

const VirtuosoTableRow = ({ context, ...props }: any) => {
	const { displayItems, rows, data, rowClassName } =
		context as VirtuosoTableContext
	const index = props['data-index']
	if (displayItems) {
		const item = displayItems[index]
		if (!item) return <tr {...props} />
		// Sub-rows have no react-table row; render a plain <tr>.
		if (item.kind === 'sub') {
			return <tr {...props} className="text-t-default" />
		}
		const row = rows[item.rowIndex]
		const rowItem = data[item.rowIndex]
		const dynamicRowClass =
			rowClassName && rowItem ? rowClassName(rowItem) : ''
		return (
			<tr
				{...props}
				{...row.getRowProps()}
				className={twMerge('text-t-default font-medium', dynamicRowClass)}
			/>
		)
	}
	const row = rows[index]
	const rowItem = data[index]
	const dynamicRowClass = rowClassName && rowItem ? rowClassName(rowItem) : ''
	return (
		<tr
			{...props}
			{...row.getRowProps()}
			className={twMerge('text-t-default font-medium', dynamicRowClass)}
		/>
	)
}

const virtuosoComponents = {
	Table: VirtuosoTable,
	TableBody: VirtuosoTableBody,
	TableHead: VirtuosoTableHead,
	TableRow: VirtuosoTableRow
}

export function GTable<T>(props: GTableProps<T>) {
	const {
		columns,
		data,
		showHeader = true,
		canBeFiltered = false,
		title,
		containerClassName,
		isLoading = false,
		sortListener,
		sort,
		endReached,
		overscan,
		emptyState,
		rowClassName,
		variant,
		renderSubRow,
		getRowId
	} = props

	const expandable = typeof renderSubRow === 'function'
	const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
	// `rows` (react-table) is derived from `rowData` state, which updates one render
	// behind the `data` prop — so after `data` shrinks (search/filter), `rows` can be
	// transiently longer than `data`. Guard against the out-of-range `data[rowIndex]`
	// so `getRowId(undefined)` never throws and unmounts the app.
	const rowKey = (rowIndex: number) => {
		const item = data[rowIndex]
		return item && getRowId ? getRowId(item) : String(rowIndex)
	}
	const toggleExpand = (key: string) =>
		setExpandedKeys((prev) => {
			const next = new Set(prev)
			if (next.has(key)) next.delete(key)
			else next.add(key)
			return next
		})

	const [rowData, setRowData] = useState<Array<any>>([])
	const [columnData, setColumnData] = useState<Array<any>>([])

	const getTableHeader = () => {
		return columns.map((column, index) => {
			return {
				accessor: `col${index + 1}`
			}
		})
	}
	const handleData = () => {
		const columnData = []
		let singleData = {}
		for (let i = 0; i < data.length; i++) {
			for (let j = 0; j < columns.length; j++) {
				singleData = {
					...singleData,
					[`col${j + 1}`]: data[i]
				}
			}
			columnData.push(singleData)
			singleData = {}
		}
		return columnData
	}

	useEffect(() => {
		if (data.length > 0) {
			setRowData(handleData())
			setColumnData(getTableHeader())
		} else {
			setRowData([])
		}
	}, [data, columns])

	const handleSort = (key: string) => {
		sortListener && sortListener(key)
	}

	const tableInstance = useTable({ columns: columnData, data: rowData })
	const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
		tableInstance

	// When expandable, each expanded row's sub-content is inserted as its OWN virtuoso
	// item right after the row, so TableVirtuoso measures its (variable) height correctly.
	// Non-expandable tables keep a 1:1 row→item mapping (null here) and are untouched.
	const displayItems: Array<{ kind: 'row' | 'sub'; rowIndex: number }> | null =
		(() => {
			if (!expandable) return null
			const items: Array<{ kind: 'row' | 'sub'; rowIndex: number }> = []
			for (let i = 0; i < rows.length; i++) {
				items.push({ kind: 'row', rowIndex: i })
				if (expandedKeys.has(rowKey(i)))
					items.push({ kind: 'sub', rowIndex: i })
			}
			return items
		})()
	const virtualCount = displayItems ? displayItems.length : rows.length
	const [scrollEdge, setScrollEdge] = useState({ start: true, end: true })
	const scrollDemoRef = useRef<HTMLDivElement>(null)
	const scrollParent = useVirtuosoScrollParent(scrollDemoRef)
	const virtuosoScrollProps = scrollParent
		? { customScrollParent: scrollParent }
		: { useWindowScroll: true as const }

	// The Custom Rules recipe measures both scroll edges: the frozen left
	// column casts its shadow once content scrolls under it (not at start),
	// the frozen right column casts while content still hides past the right
	// edge. Measured on scroll, on resize, and after every render, because the
	// rows load async and change scrollWidth without an element resize.
	const measureScrollEdges = useCallback(() => {
		const el = scrollDemoRef.current
		if (!el) return
		const start = el.scrollLeft <= 0
		const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
		setScrollEdge((p) =>
			p.start === start && p.end === end ? p : { start, end }
		)
	}, [])

	useLayoutEffect(() => {
		measureScrollEdges()
		const el = scrollDemoRef.current
		if (!el || typeof ResizeObserver === 'undefined') return
		const ro = new ResizeObserver(() => measureScrollEdges())
		ro.observe(el)
		return () => ro.disconnect()
	}, [measureScrollEdges])

	useEffect(() => {
		measureScrollEdges()
	})

	const isScrollLeft = !scrollEdge.start
	const handleScroll = () => measureScrollEdges()

	return (
		<div className={twMerge('bg-white rounded w-full', containerClassName)}>
			{title && (
				<div className="mb-4">
					<span className="text-t-desc-gray font-medium">{title}</span>
				</div>
			)}
			<div
				className={twMerge(
					'inline-block min-w-full align-middle overflow-x-auto',
					// `overflow-y-clip` stops the implicit vertical scroll container
					// that `overflow-x-auto` would otherwise coerce (visible -> auto on
					// the cross axis), which trapped the wheel and made the page
					// "scroll twice". Vertical scroll stays on <main>. DS variant only.
					// `pb-px` gives the collapsed table's OUTER bottom border room:
					// border-collapse centers the last row's 1px rule on the table's
					// bottom grid line, so its lower half paints outside the table box.
					// Without the padding the clip eats that half and the last rule
					// renders thinner than the row dividers.
					variant === 'ds'
						? 'ds-table-scroll rounded-t-xl overflow-y-clip pb-px'
						: ''
				)}
				id="scrollDemo"
				ref={scrollDemoRef}
				onScroll={handleScroll}
			>
				{((!isLoading && rows?.length > 0) || canBeFiltered) && (
					<TableVirtuoso
						{...virtuosoScrollProps}
						totalCount={virtualCount}
						endReached={() => {
							if (endReached) endReached()
						}}
						{...(overscan && { overscan })}
						context={{
							tableProps: getTableProps(),
							bodyProps: getTableBodyProps(),
							showHeader,
							displayItems,
							rows,
							data,
							rowClassName
						}}
						components={virtuosoComponents as any}
						fixedHeaderContent={() => {
							if (showHeader) {
								return headerGroups.map((headerGroup) => (
									<tr {...headerGroup.getHeaderGroupProps()}>
										{expandable && (
											<th
												aria-hidden
												className={twMerge(
													variant === 'ds'
														? 'w-[30px] pl-[6px] pr-0 py-3 border-b border-b-[rgb(var(--cg-border))] cg-bg-canvas'
														: 'w-10 px-2 py-3 border-b border-b-t-checkbox'
												)}
											/>
										)}
										{headerGroup.headers.map((column, index) => (
											<TableHeader
												key={index}
												column={columns[index]}
												reactColumn={column}
												sort={sort}
												onSort={handleSort}
												isScrolling={isScrollLeft}
								isAtEnd={scrollEdge.end}
												variant={variant}
											/>
										))}
									</tr>
								))
							}
						}}
						itemContent={(index) => {
							if (displayItems) {
								const item = displayItems[index]
								if (!item) return null
								// Full-width sub-row spanning the chevron column + every data column.
								if (item.kind === 'sub') {
									const subItem = data[item.rowIndex]
									if (!subItem) return null
									return (
										<td colSpan={columns.length + 1} className="p-0 bg-white">
											{renderSubRow?.(subItem)}
										</td>
									)
								}
								const row = rows[item.rowIndex]
								prepareRow(row)
								const key = rowKey(item.rowIndex)
								return (
									<>
										<ExpandToggleCell
											isDs={variant === 'ds'}
											expanded={expandedKeys.has(key)}
											onToggle={() => toggleExpand(key)}
										/>
										{row.cells.map((cell, i) => (
											<TableBody
												key={i}
												cell={cell}
												column={columns[i]}
												isScrolling={isScrollLeft}
								isAtEnd={scrollEdge.end}
												variant={variant}
											/>
										))}
									</>
								)
							}
							const row = rows[index]
							prepareRow(row)
							return row.cells.map((cell, index) => {
								return (
									<TableBody
										key={index}
										cell={cell}
										column={columns[index]}
										isScrolling={isScrollLeft}
								isAtEnd={scrollEdge.end}
										variant={variant}
									/>
								)
							})
						}}
					/>
				)}
				{isLoading ? (
					<div className="pb-12 pt-6">
						<DotMatrix size="sm" label="Loading forms" />
					</div>
				) : data.length === 0 ? (
					emptyState ? (
						emptyState
					) : (
						<div className="flex flex-col items-center justify-center w-full pb-8 pt-8 text-center">
							<RiErrorWarningLine className="w-8 h-8 text-gray-500" />
							<h3 className="mt-2 text-md font-medium text-gray-700">
								No Results
							</h3>
						</div>
					)
				) : null}
			</div>
		</div>
	)
}
